import { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { useLazyGetPersonByIdQuery } from "@api/personasApi";
import { PERSON_CATALOG } from "@helpers/constantes";

/**
 * Tipo de una persona (de `personas/{id}`) en minúsculas, para textos como "Validación para consulta buró del
 * representante legal". Si tiene varios tipos y uno es accionista mayoritario, se usa ese. `null` mientras se
 * consulta; `""` sin persona o si falla (solo Sentry), para que la pantalla use su texto genérico.
 */
const useTipoPersona = (personId: string | number | undefined, component: string) => {
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [tipoPersona, setTipoPersona] = useState<string | null>(null);

  const fetchTipoPersona = useCallback(async () => {
    if (!personId) {
      setTipoPersona("");
      return;
    }
    try {
      const person = await getPersonById(String(personId)).unwrap();
      const tipos = person.person_types_detail ?? [];
      const accionista = tipos.find((item) => item.code === PERSON_CATALOG.accionista_mayoritario.code);
      // PTAM se nombra igual en todas las pantallas, sin importar el nombre que traiga el catálogo.
      if (accionista) {
        setTipoPersona("accionista mayoritario");
        return;
      }
      setTipoPersona(tipos[0]?.name?.trim().toLowerCase() ?? "");
    } catch (error) {
      Sentry.captureException(error, { extra: { component: `${component}-tipo_persona` } });
      setTipoPersona("");
    }
  }, [personId, getPersonById, component]);

  useEffect(() => {
    fetchTipoPersona();
  }, [fetchTipoPersona]);

  return tipoPersona;
};

export default useTipoPersona;
